import { z } from "zod";
import {
  emailSchema,
  passwordSchema,
  personNameSchema,
  phoneSchema,
  requiredNameSchema,
} from "./common";

/**
 * User validation schemas
 */

/**
 * Schema for profile update validation
 * Matches backend PATCH /api/v1/user/{uuid} (UserUpdate) endpoint
 * Backend expects optional fields that can be null
 */
export const profileUpdateSchema = z.object({
  firstname: personNameSchema.nullable().optional(),
  lastname: personNameSchema.nullable().optional(),
  // Reuse common phoneSchema; add .nullable() for backend PATCH compatibility
  phone: phoneSchema.nullable(),
});

/**
 * Schema for password change validation
 */
export const passwordChangeSchema = z
  .object({
    currentPassword: z.string().min(1, "Current password is required"),
    newPassword: passwordSchema,
    confirmPassword: z.string().min(1, "Please confirm your new password"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

/**
 * Schema for user update (admin functionality)
 */
export const userUpdateSchema = z.object({
  firstname: personNameSchema.optional(),
  lastname: personNameSchema.optional(),
  // Reuse common phoneSchema for consistent phone validation across forms
  phone: phoneSchema,
  role: z.enum(["Member", "Admin", "System User", "Superuser"]).optional(),
});

/**
 * Schema for sign in validation (client-side validation only).
 * Only checks non-empty -- password complexity is NOT enforced at login
 * so legacy users whose passwords predate current policy can still sign in.
 * The backend is the source of truth for credential verification.
 */
export const signinSchema = z.object({
  email: emailSchema,
  password: z
    .string()
    .min(1, "Password is required")
    .max(100, "Password must be less than 100 characters"),
});

/**
 * Schema for user signup validation (matches backend UserRegisterWithInvite).
 * Email is derived by the backend from the invitation token and is not part
 * of the request payload.
 */
export const signupSchema = z
  .object({
    firstname: requiredNameSchema,
    lastname: requiredNameSchema,
    phone: phoneSchema.optional(),
    password: passwordSchema,
    confirmPassword: z.string().min(1, "Please confirm your password"),
    token: z.string().min(1, "Invitation token is required"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

/**
 * Schema for password recovery validation
 */
export const forgotPasswordSchema = z.object({
  email: emailSchema,
});

/**
 * Schema for password reset validation
 */
export const resetPasswordSchema = z
  .object({
    token: z.string().min(1, "Reset token is required"),
    newPassword: passwordSchema,
    confirmPassword: z.string().min(1, "Please confirm your new password"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

/**
 * Schema for invitation token verification response.
 * Validates the data returned by the backend verify-invitation endpoint.
 */
export const invitationDataSchema = z.object({
  firstname: z.string(),
  lastname: z.string(),
  email: z.string(),
  role: z.string(),
});

/**
 * Permission level enum — mirrors PermissionLevel in types/central/permissions.ts
 */
export const permissionLevelSchema = z.enum(["read", "edit", "manage", "none"]);

/**
 * Schema for Teams Manage User modal (role + facility permissions).
 * Only Admin / Member are assignable on the Teams page.
 */
export const userManageSchema = z.object({
  role: z.enum(["Admin", "Member"]),
  permissions: z.record(z.string(), permissionLevelSchema),
});

// Type inference for all user schemas
export type SigninForm = z.infer<typeof signinSchema>;
export type SignupForm = z.infer<typeof signupSchema>;
export type ForgotPasswordForm = z.infer<typeof forgotPasswordSchema>;
export type ResetPasswordForm = z.infer<typeof resetPasswordSchema>;
export type ProfileUpdateForm = z.infer<typeof profileUpdateSchema>;
export type PasswordChangeForm = z.infer<typeof passwordChangeSchema>;
export type UserUpdateForm = z.infer<typeof userUpdateSchema>;
export type InvitationData = z.infer<typeof invitationDataSchema>;
export type UserManageForm = z.infer<typeof userManageSchema>;
